import { useEffect, useMemo } from "react";
import { useDispatch } from "react-redux";
import MainAdsLayout from "@/_component/MainAdsLayout";
import { Loader, PageTitle } from "@/_component/Common";
import { useLanguageContext } from "@/hooks";
import { fetchUserAccess, useUserStore } from "@/store/slices";
import { ChooseCategoryContainer } from "../../choose-category/ChooseCategoryContainer";
import {
    OnboardingCategoryStrategy,
    type OnboardingCategoryAccessType,
} from "../../choose-category/strategies/OnboardingCategoryStrategy";

type OnboardingCategorySelectionProps = {
    accessType: OnboardingCategoryAccessType;
};

const OnboardingCategorySelection = ({
    accessType,
}: OnboardingCategorySelectionProps) => {
    const dispatch = useDispatch();
    const { language } = useLanguageContext();
    const { userAccessList, userAccessLoaded, userAccessLoading } = useUserStore();

    useEffect(() => {
        if (!userAccessLoaded && !userAccessLoading) {
            dispatch(fetchUserAccess() as any);
        }
    }, [dispatch, userAccessLoaded, userAccessLoading]);

    const registeredCategoryIds = useMemo(
        () =>
            userAccessList.map((access: any) =>
                String(access.category_id ?? access.categoryId)
            ),
        [userAccessList]
    );
    const strategy = useMemo(
        () =>
            new OnboardingCategoryStrategy(
                accessType,
                registeredCategoryIds
            ),
        [accessType, registeredCategoryIds]
    );
    const strategyKey = `${accessType}:${userAccessLoaded}:${registeredCategoryIds.join(
        ","
    )}`;

    return (
        <MainAdsLayout sideBanner>
            <PageTitle
                headingPart1={language("label.choose_your", "Choose Your")}
                headingPart2={language(
                    accessType === "dealer"
                        ? "access.dealer"
                        : "access.service_provider",
                    accessType === "dealer" ? "Dealer" : "Service Provider"
                )}
            />
            <div className="pe-lg-3">
                {!userAccessLoaded ? (
                    <Loader />
                ) : (
                    <ChooseCategoryContainer
                        key={strategyKey}
                        strategy={strategy}
                        emptyContent={
                            <p>
                                {language(
                                    "label.no_category",
                                    "No categories found"
                                )}
                            </p>
                        }
                    />
                )}
            </div>
        </MainAdsLayout>
    );
};

export default OnboardingCategorySelection;
